import React, { useEffect, useMemo, useState } from 'react';
import { rupiah } from './api';
import { inputClass } from './ui';
import { InfiniteFooter, useInfiniteList } from './infiniteList';
import { effectiveUnitPrice, estimateSubtotal } from '../pricing';

// Pemilih produk BERSAMA untuk halaman "Buat pesanan" dan sheet "Ubah produk" di detail pesanan admin.
// - HP: daftar NAMA produk saja (tanpa harga per baris) dengan kolom cari di atas; tiap baris = centang + nama +
//   stepper jumlah. Mencentang = qty 1; qty minimal 1 (tombol kurang nonaktif di 1 — batalkan lewat centang);
//   qty maksimal 999. md ke atas: daftar yang sama + kolom harga satuan.
// - Produk dimuat dari GET /api/admin/products?status=active&q= (gulir tanpa batas, pencarian di server).
// - value: [{productId, qty, name, unit, price, tiers, fallbackUnitPrice?, unavailable?}] (urutan pilih).
//   Harga & jenjang produk dipakai HANYA untuk total sementara (server tetap otoritas).

export const MAX_QTY = 999;
export const MAX_LINES = 50;

export const clampQty = (raw) => {
  const n = Math.floor(Number(String(raw ?? '').replace(/[^0-9]/g, '')));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(MAX_QTY, n);
};

// Ringkasan pilihan: jumlah produk, jumlah item, total sementara.
export const summarizeSelection = (value) => ({
  lines: (value || []).length,
  items: (value || []).reduce((n, l) => n + (Number(l.qty) || 0), 0),
  subtotal: estimateSubtotal(value),
});

const lineFromProduct = (p, qty = 1) => ({
  productId: p.id,
  qty,
  name: p.name,
  unit: p.unit,
  price: p.price,
  tiers: p.tiers || [],
});

const Stepper = ({ qty, checked, name, onQty, onCheck }) => {
  const [text, setText] = useState(String(qty || ''));
  useEffect(() => setText(checked ? String(qty) : ''), [qty, checked]);
  const btn =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div className="flex shrink-0 items-center gap-1" role="group" aria-label={`Jumlah ${name}`}>
      <button type="button" className={btn} aria-label={`Kurangi ${name}`} disabled={!checked || qty <= 1} onClick={() => onQty(qty - 1)}>
        −
      </button>
      <input
        className="h-9 w-12 rounded-md border border-gray-300 text-center text-base"
        inputMode="numeric"
        aria-label={`Jumlah ${name}`}
        value={text}
        placeholder="0"
        disabled={!checked}
        onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
        onBlur={() => onQty(clampQty(text))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onQty(clampQty(text));
          }
        }}
      />
      <button
        type="button"
        className={btn}
        aria-label={`Tambah ${name}`}
        disabled={checked && qty >= MAX_QTY}
        onClick={() => (checked ? onQty(qty + 1) : onCheck(true))}
      >
        +
      </button>
    </div>
  );
};

const Row = ({ line, product, checked, onToggle, onQty, testId }) => {
  const name = product?.name || line?.name || '';
  const qty = line?.qty || 0;
  const price = product?.price ?? line?.price;
  const tiers = product?.tiers ?? line?.tiers;
  const unit = product?.unit ?? line?.unit;
  const unitPrice = price !== undefined && price !== null ? effectiveUnitPrice(price, tiers, Math.max(qty, 1)) : line?.fallbackUnitPrice;
  return (
    <li className={`flex items-center gap-3 py-2 ${checked ? 'bg-purple-50/60' : ''}`} data-testid={testId}>
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 px-1">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-gray-300 text-purple-700 focus:ring-purple-500"
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`Pilih ${name}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-gray-900 [overflow-wrap:anywhere] line-clamp-2">{name}</span>
          {line?.unavailable && <span className="block text-xs text-red-700">Produk tidak tersedia lagi — hapus centang</span>}
        </span>
        {/* Harga hanya md ke atas (di HP total sementara cukup di bar bawah). */}
        <span className="hidden w-32 shrink-0 text-right text-xs text-gray-600 md:block">
          {Number.isFinite(Number(unitPrice)) && unitPrice !== undefined ? `${rupiah(unitPrice)}${unit ? ` / ${unit}` : ''}` : ''}
        </span>
      </label>
      <Stepper qty={qty} checked={checked} name={name} onQty={onQty} onCheck={onToggle} />
    </li>
  );
};

// Daftar produk aktif (cari server) + daftar "Terpilih" di atasnya.
export const ProductPicker = ({ value, onChange, autoFocus = false }) => {
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(draft.trim()), 300);
    return () => clearTimeout(t);
  }, [draft]);
  const list = useInfiniteList('/products', { status: 'active', q });
  const byId = useMemo(() => new Map((value || []).map((l) => [l.productId, l])), [value]);

  // Produk termuat melengkapi harga/jenjang baris terpilih (mis. pre-isi dari pesanan).
  useEffect(() => {
    if (!value?.length || !list.items.length) return;
    let changed = false;
    const next = value.map((l) => {
      const p = list.items.find((x) => x.id === l.productId);
      if (p && (l.price !== p.price || l.tiers !== p.tiers)) {
        changed = true;
        return { ...l, name: p.name, unit: p.unit, price: p.price, tiers: p.tiers, unavailable: false };
      }
      return l;
    });
    if (changed) onChange(next);
  }, [list.items]); // sengaja hanya list.items: value/onChange dibaca terbaru saat produk termuat

  const setLine = (productId, patch) => onChange(value.map((l) => (l.productId === productId ? { ...l, ...patch } : l)));
  const toggle = (product, line) => (on) => {
    const id = product?.id ?? line.productId;
    if (!on) {
      onChange(value.filter((l) => l.productId !== id));
      return;
    }
    if (byId.has(id) || value.length >= MAX_LINES) return;
    onChange([...value, product ? lineFromProduct(product, 1) : { ...line, qty: 1 }]);
  };

  const sum = summarizeSelection(value);
  return (
    <div data-testid="product-picker">
      {value.length > 0 && (
        <div className="mb-3">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Terpilih ({sum.lines} produk, {sum.items} item)
          </h3>
          <ul className="divide-y divide-gray-100 rounded-lg border border-purple-200" data-testid="picker-selected">
            {value.map((l) => (
              <Row
                key={l.productId}
                line={l}
                checked
                testId="picker-selected-row"
                onToggle={toggle(null, l)}
                onQty={(n) => setLine(l.productId, { qty: clampQty(n) })}
              />
            ))}
          </ul>
          {value.length >= MAX_LINES && <p className="mt-1 text-xs text-amber-800">Maksimal {MAX_LINES} produk berbeda per pesanan.</p>}
        </div>
      )}
      <label className="block">
        <span className="sr-only">Cari produk</span>
        <input
          type="search"
          className={`${inputClass} text-base`}
          placeholder="Cari produk..."
          value={draft}
          maxLength={150}
          autoFocus={autoFocus}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="picker-search"
        />
      </label>
      {list.error && <p className="mt-2 text-sm text-red-700">{list.error.message}</p>}
      <ul className="mt-2 divide-y divide-gray-100" data-testid="picker-results">
        {!list.loading && !list.error && list.items.length === 0 && (
          <li className="py-4 text-center text-sm text-gray-500">{q ? 'Produk tidak ditemukan.' : 'Belum ada produk aktif.'}</li>
        )}
        {list.items.map((p) => {
          const line = byId.get(p.id);
          return (
            <Row
              key={p.id}
              line={line}
              product={p}
              checked={!!line}
              testId="picker-row"
              onToggle={toggle(p, line)}
              onQty={(n) => setLine(p.id, { qty: clampQty(n) })}
            />
          );
        })}
      </ul>
      <InfiniteFooter list={list} noun="produk" />
    </div>
  );
};

export default ProductPicker;
